import { Flag, MoreVert } from "@mui/icons-material";
import {
  Alert,
  AlertTitle,
  Breadcrumbs,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { createFileRoute, notFound, redirect } from "@tanstack/react-router";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import { useId, useState } from "react";
import { MonthwiseActivityTable } from "@/components/activity/months-activity";
import { ErrorDisplay } from "@/components/core/error-display";
import { CardLoadingSkeleton } from "@/components/core/loading-skeleton";
import { Link } from "@/components/core/tanstack-link";
import { CustomerAddNote } from "@/components/customer/add-note";
import { CustomerSetNextAction } from "@/components/customer/set-next-action";
import { DownloadInvoice } from "@/components/invoices/download-invoice";
import { InvoicesTable } from "@/components/tables/invoices";
import { DeeplinkInvoice } from "@/helpers/deeplink";
import { ActivityStatusToChip, providerToName } from "@/helpers/labels";
import {
  ActivityStatus,
  getGetInvoiceQueryKey,
  getGetWorkflowsQueryKey,
  getInvoice,
  getWorkflows,
  InvoiceDetailsOut,
  invoiceDownload,
  NoteTroveType,
  useGetInvoice,
  useGetWorkflows,
} from "@/models/backend";
import { AxiosError, isAxiosError } from "axios";
import { CopyTextField } from "@/components/core/copy-textfield";
import { queryClient } from "@/helpers/tanstack-client";

dayjs.extend(relativeTime);

export const Route = createFileRoute("/_authenticated/_contained/invoices/$invoiceId")({
  component: RouteComponent,
  loader: async ({ params: { invoiceId } }) => {
    try {
      await Promise.all([
        queryClient.ensureQueryData({
          queryFn: getWorkflows,
          queryKey: getGetWorkflowsQueryKey(),
        }),
        queryClient
          .ensureQueryData({
            queryFn: (signal) => getInvoice(invoiceId, signal),
            queryKey: getGetInvoiceQueryKey(invoiceId),
          })
          .catch(async (e) => {
            if (isAxiosError(e) && e.status == 404) {
              if (e.response?.data?.detail?.customer_id) {
                throw redirect({
                  to: "/customers/$customerId",
                  params: { customerId: e.response.data.detail.customer_id },
                  search: { tab: "policies" },
                });
              } else {
                throw e;
              }
            }
          }),
      ]);
    } catch (e) {
      if (isAxiosError(e) && e.status == 404) {
        throw notFound();
      } else {
        throw e;
      }
    }
  },
});
const MoreButton = ({
  invoice,
  setAddNote,
}: {
  invoice: InvoiceDetailsOut;
  setAddNote: (note: { type?: NoteTroveType }) => void;
}) => {
  const [menuOpen, setMenuOpen] = useState<HTMLButtonElement | null>(null);
  const menuId = useId();
  const [showCopy, setShowCopy] = useState(false);

  return (
    <>
      <Button
        sx={{ minWidth: "0px", padding: "1px" }}
        variant="outlined"
        color="secondary"
        aria-controls={menuId}
        aria-haspopup="true"
        aria-expanded={menuOpen ? "true" : undefined}
        onClick={(e) => setMenuOpen((o) => (o ? null : e.currentTarget))}
      >
        <MoreVert />
      </Button>
      <Menu
        id={menuId}
        anchorEl={menuOpen}
        open={menuOpen !== null}
        onClose={() => setMenuOpen(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <MenuItem component={DeeplinkInvoice} invoice={invoice}>
          <ListItemText>View in {providerToName(invoice.type)}</ListItemText>
        </MenuItem>
        {!invoice.in_dispute ? (
          <MenuItem
            onClick={() => {
              setAddNote({ type: NoteTroveType.Marked_disputed });
              setMenuOpen(null);
            }}
          >
            <ListItemText>Mark as disputed</ListItemText>
          </MenuItem>
        ) : (
          <MenuItem
            onClick={() => {
              setAddNote({ type: NoteTroveType.Dispute_removed });
              setMenuOpen(null);
            }}
          >
            <ListItemText>Remove dispute</ListItemText>
          </MenuItem>
        )}
        {invoice.portal_url != null && (
          <MenuItem
            onClick={() => {
              setShowCopy(true);
              setMenuOpen(null);
            }}
          >
            <ListItemText>Share invoice link</ListItemText>
          </MenuItem>
        )}
      </Menu>
      <Dialog open={showCopy} onClose={() => setShowCopy(false)}>
        <DialogTitle>Share invoice link</DialogTitle>
        <DialogContent>
          <Stack spacing={2}>
            <Typography>Share this link with your customer:</Typography>
            <CopyTextField value={invoice.portal_url ?? ""} sx={{ maxWidth: "600px" }} />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowCopy(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

function RouteComponent() {
  const { invoiceId } = Route.useParams();
  const [addNote, setAddNote] = useState<{ type?: NoteTroveType; workflowId?: string } | null>(
    null,
  );
  const {
    data: invoice,
    isLoading: isLoadingInvoice,
    isError: isErrorInvoice,
    error: invoiceError,
    refetch: refetchInvoice,
  } = useGetInvoice(invoiceId);
  const [showNextActivity, setShowNextActivity] = useState(false);
  const { data: workflows } = useGetWorkflows();

  if (isErrorInvoice) {
    if (invoiceError instanceof AxiosError && invoiceError.status == 404) {
      throw notFound();
    }
    return (
      <ErrorDisplay
        title="Failed to load invoice"
        error={invoiceError}
        onRetry={() => refetchInvoice()}
      />
    );
  }

  if (isLoadingInvoice) {
    return <CardLoadingSkeleton />;
  }

  if (!invoice) {
    return null;
  }

  return (
    <Stack spacing={4}>
      <Breadcrumbs separator=">" aria-label="breadcrumb">
        <Link to="/customers">Customers</Link>
        {invoice.parents.map((parent) => (
          <Link
            key={parent.id}
            to="/customers/$customerId"
            params={{ customerId: parent.id }}
            search={{ tab: "invoices" }}
          >
            {parent.name}
          </Link>
        ))}
        <Typography>{invoice.number}</Typography>
      </Breadcrumbs>
      <Stack spacing={1} direction="row" sx={{ justifyContent: "space-between" }}>
        <Stack spacing={1} sx={{ alignItems: "start" }}>
          <Typography variant="h3">
            {invoice.number}{" "}
            {invoice.in_dispute && (
              <Tooltip title="In dispute - will still be included in client emails">
                <Flag color="error" fontSize="medium" />
              </Tooltip>
            )}
          </Typography>
          {invoice.activity_status && <ActivityStatusToChip status={invoice.activity_status} />}
          {invoice.frequency && (
            <Typography variant="body2">Recurring invoice: {invoice.frequency}</Typography>
          )}
        </Stack>
        <Stack spacing={1}>
          <Stack
            spacing={1}
            direction="row"
            sx={{
              ml: "auto",
              textWrap: "nowrap",
              flexWrap: "wrap",
              justifyContent: "end",
            }}
          >
            <TextField
              select
              value={
                invoice.activity_status == ActivityStatus.excluded
                  ? invoice.activity_status
                  : invoice.activity_status == ActivityStatus.customer_excluded
                    ? invoice.activity_status
                    : invoice.workflow?.id
              }
              disabled={invoice.activity_status === ActivityStatus.customer_excluded}
              label={
                invoice.activity_status != ActivityStatus.excluded &&
                invoice.activity_status != ActivityStatus.customer_excluded &&
                invoice.workflow == null
                  ? "Workflow"
                  : ""
              }
              sx={{ "& .MuiSelect-select": { py: "7px" }, minWidth: "200px" }}
              slotProps={{ inputLabel: { shrink: true } }}
              onChange={(e) => {
                if (e.target.value == ActivityStatus.excluded) {
                  setAddNote({ type: NoteTroveType.Excluded_invoice });
                } else {
                  setAddNote({ type: NoteTroveType.Enrolled_invoice, workflowId: e.target.value });
                }
              }}
            >
              {workflows?.map((workflow) => (
                <MenuItem key={workflow.id} value={workflow.id}>
                  {workflow.name}
                </MenuItem>
              )) ?? <MenuItem value={invoice.workflow?.id}>{invoice.workflow?.name}</MenuItem>}
              <Divider />
              <MenuItem value={ActivityStatus.excluded}>Excluded</MenuItem>
              {invoice.activity_status == ActivityStatus.customer_excluded && (
                <MenuItem value={ActivityStatus.customer_excluded}>Customer excluded</MenuItem>
              )}
            </TextField>
            <Button
              variant="contained"
              color="info"
              onClick={() => {
                setAddNote({});
              }}
            >
              Add note
            </Button>
            <MoreButton invoice={invoice} setAddNote={setAddNote} />
          </Stack>
          <Stack direction="row" spacing={2} sx={{ justifyContent: "end" }}>
            <DownloadInvoice invoice={invoice} download={() => invoiceDownload(invoiceId)} />
          </Stack>
        </Stack>
      </Stack>

      {invoice.activity_status === ActivityStatus.completed && invoice.next_action === null && (
        <Alert
          severity="warning"
          action={
            <Button
              size="small"
              variant="outlined"
              color="warning"
              sx={{
                color: "var(--mui-palette-Alert-warningColor)",
                borderColor: "var(--mui-palette-Alert-warningColor)",
              }}
              onClick={() => setShowNextActivity(true)}
            >
              Set next activity
            </Button>
          }
        >
          <AlertTitle>Invoice completed workflow</AlertTitle>
          There are no further actions scheduled for this invoice. Specify next activity.
        </Alert>
      )}
      <Stack spacing={2}>
        <Stack
          direction="row"
          sx={{ alignItems: "center", justifyContent: "space-between", width: "100%" }}
        >
          <Typography variant="h6">Invoice</Typography>
        </Stack>
        <InvoicesTable invoiceId={invoice.id} />
      </Stack>
      {invoice.customer && (
        <Stack spacing={2}>
          <Typography variant="h6">All invoice activity</Typography>
          <MonthwiseActivityTable customerId={invoice.customer.id} invoiceId={invoice.id} />
        </Stack>
      )}
      {invoice.customer && (
        <CustomerAddNote
          customer={invoice.customer}
          open={addNote !== null}
          onClose={() => setAddNote(null)}
          selectedInvoices={[invoice]}
          forceType={addNote?.type}
          workflowId={addNote?.workflowId}
        />
      )}
      {invoice.customer && (
        <CustomerSetNextAction
          customerId={invoice.customer.id}
          open={showNextActivity}
          onClose={() => setShowNextActivity(false)}
          selectedInvoices={[invoice.id]}
        />
      )}
    </Stack>
  );
}
